"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Select } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon } from "@/components/ui/icon";
import type { InputVariantProps } from "@/components/ui/input";
import { useMembers } from "@/features/settings/hooks/use-members";
import { initials } from "@/lib/string/initials";
import { memberDisplayName } from "@/lib/string/user-display";
import type { UserResponse } from "@/lib/api/model/userResponse";

const MEMBER_LIST_PARAMS = { per_page: 100, status: "active" } as const;
const EMPTY_MEMBERS: UserResponse[] = [];

export interface UsersSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  excludeIds?: string[];
  filter?: (member: UserResponse) => boolean;
  emptyDescription?: ReactNode;
  maxVisibleAvatars?: number;
  variant?: InputVariantProps["variant"];
  size?: InputVariantProps["size"];
}

export function UsersSelect({
  value,
  onChange,
  placeholder = "Select users...",
  disabled,
  className,
  excludeIds,
  filter,
  emptyDescription,
  maxVisibleAvatars = 3,
  variant,
  size,
}: UsersSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useMembers(MEMBER_LIST_PARAMS);
  const members = data?.members ?? EMPTY_MEMBERS;

  const selectedSet = useMemo(() => new Set(value), [value]);

  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return members.filter((m) => {
      if (excludeSet.has(m.id)) return false;
      if (filter && !filter(m)) return false;
      if (!q) return true;
      const name = memberDisplayName(m).toLowerCase();
      return name.includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [members, excludeSet, filter, debouncedQuery]);

  const selectedMembers = useMemo(
    () => members.filter((m) => selectedSet.has(m.id)),
    [members, selectedSet],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset keyboard highlight whenever the filtered list shape changes
    setHighlightedIndex(-1);
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  const handleToggle = useCallback(
    (member: UserResponse) => {
      if (selectedSet.has(member.id)) {
        onChange(value.filter((id) => id !== member.id));
      } else {
        onChange([...value, member.id]);
      }
    },
    [onChange, selectedSet, value],
  );

  const visibleAvatars = selectedMembers.slice(0, maxVisibleAvatars);
  const overflow = selectedMembers.length - visibleAvatars.length;
  const summary = selectedMembers.map((m) => memberDisplayName(m)).join(", ");
  const hasSelection = selectedMembers.length > 0;
  const showPending = isFetching && members.length === 0;
  const showEmpty = !showPending && filtered.length === 0;

  return (
    <Select.Root
      open={open}
      onOpenChange={handleOpenChange}
      highlightedIndex={highlightedIndex}
      onHighlightedIndexChange={setHighlightedIndex}
    >
      <Select.Trigger
        disabled={disabled}
        className={className}
        variant={variant}
        size={size}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {hasSelection ? (
            <div className="flex -space-x-1.5 shrink-0">
              {visibleAvatars.map((m) => (
                <Avatar
                  key={m.id}
                  className="size-[18px] shrink-0 ring-2 ring-surface-elevated"
                >
                  <AvatarImage src={m.avatar_url ?? undefined} alt="" />
                  <AvatarFallback className="text-[8px] font-medium leading-none">
                    {initials(memberDisplayName(m))}
                  </AvatarFallback>
                </Avatar>
              ))}
              {overflow > 0 && (
                <div className="flex items-center justify-center size-[18px] rounded-full bg-contrast/8 text-[9px] font-medium text-contrast/70 ring-2 ring-surface-elevated shrink-0">
                  +{overflow}
                </div>
              )}
            </div>
          ) : (
            <div className="opacity-40">
              <Icon name="IconPeople" size={14} className="text-heading" />
            </div>
          )}
          <span
            className={`flex-1 text-left truncate text-sm ${
              hasSelection ? "text-heading" : "text-contrast/50"
            }`}
          >
            {hasSelection ? summary : placeholder}
          </span>
          <div className="opacity-40">
            <Icon
              name="IconChevronGrabberVertical"
              size={14}
              className="text-heading"
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content>
        <Select.Search
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search..."
          loading={isFetching}
        />

        <Select.List>
          {showPending ? (
            <Select.ListPending />
          ) : showEmpty ? (
            <div className="flex flex-col items-center justify-center gap-1 py-8 px-3">
              <div className="opacity-30 mb-1">
                <Icon name="IconPeople" size={32} className="text-heading" />
              </div>
              {emptyDescription ? (
                <Select.EmptyDescription>
                  {emptyDescription}
                </Select.EmptyDescription>
              ) : null}
            </div>
          ) : (
            filtered.map((m, i) => {
              const name = memberDisplayName(m);
              const isSelected = selectedSet.has(m.id);
              const sublabel = m.role === "admin" ? "Admin" : undefined;
              return (
                <Select.Item
                  key={m.id}
                  index={i}
                  selected={isSelected}
                  onSelect={() => handleToggle(m)}
                >
                  <Select.ItemIcon>
                    <Avatar className="size-5 shrink-0">
                      <AvatarImage src={m.avatar_url ?? undefined} alt="" />
                      <AvatarFallback className="text-[8px] font-medium leading-none">
                        {initials(name)}
                      </AvatarFallback>
                    </Avatar>
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={isSelected}>
                    {name || "Unnamed"}
                  </Select.ItemLabel>
                  {sublabel && (
                    <span className="text-contrast/40 shrink-0 text-xs">
                      {sublabel}
                    </span>
                  )}
                  {isSelected && <Select.ItemCheck />}
                </Select.Item>
              );
            })
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
